<script setup>
import { computed, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStageTimer } from 'dashboard/composables/kanban/useStageTimer';
import {
  useKanbanCard,
  getChannelColorClass,
} from 'dashboard/composables/kanban/useKanbanCard';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  stageConfig: {
    type: Object,
    required: true,
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnelId: {
    type: Number,
    required: false,
    default: null,
  },
  stageName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'click',
  'open-conversation',
  'open-crm',
  'open-side-panel',
  'drag-start',
  'remove-from-funnel',
]);

const { t } = useI18n();

// Estado para controlar hover
const isHovered = ref(false);

// Use composable para todas as computed properties
const conversationRef = toRef(props, 'conversation');
const mappingRef = toRef(props, 'mapping');
const funnelIdRef = toRef(props, 'funnelId');

const {
  currentContact,
  inbox,
  inboxChannelIcon,
  assignedAgents,
  visibleAgents,
  additionalAgentsCount,
  activeLabels,
  statusColor,
  statusLabel,
  totalValue,
  appointmentDateTime,
  messagePreview,
} = useKanbanCard(conversationRef, mappingRef, funnelIdRef);

// Timer computations
const { compactTime, getSLAClass, isSLABreached } = useStageTimer(
  props.mapping?.stage_entered_at || props.conversation.stage_entered_at,
  props.mapping?.timer_data || {}
);

// Verificar se tem SLA violado
const hasSLAViolation = computed(() =>
  isSLABreached(props.stageConfig.sla_hours)
);

// Cor do estágio
const stageColor = computed(() => props.stageConfig?.color || '#6366f1');

// Estilo de glow baseado na cor do estágio
const cardGlowStyle = computed(() => {
  const color = stageColor.value;
  return {
    '--stage-color': color,
    '--stage-color-glow': `${color}20`,
  };
});

// Formatar valor total de forma compacta
const formattedTotalValue = computed(() => {
  if (!totalValue.value) return null;
  const value = parseFloat(totalValue.value);
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1)}k`;
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
});

// Formatar agendamento de forma compacta
const compactAppointment = computed(() => {
  if (!appointmentDateTime.value || appointmentDateTime.value === '--')
    return null;
  return appointmentDateTime.value;
});

// Event handlers
const handleCardClick = () => {
  // Emite evento para abrir o painel lateral
  emit('click', props.conversation);
};

const handleOpenConversation = event => {
  event.stopPropagation();
  emit('open-conversation', props.conversation);
};

const handleOpenCRM = event => {
  event.stopPropagation();
  emit('open-crm', props.conversation, currentContact.value);
};

const handleRemoveFromFunnel = event => {
  event.stopPropagation();
  const funnelName = props.conversation.funnel_name || 'este funil';

  if (
    window.confirm(
      `Remover do Funil\n\nTem certeza que deseja remover esta conversa do funil "${funnelName}"?`
    )
  ) {
    emit('remove-from-funnel', props.conversation);
  }
};

const handleDragStart = event => {
  event.dataTransfer.effectAllowed = 'move';

  const dragData = {
    conversationId: props.conversation.id,
    fromColumn: props.stageName,
  };

  event.dataTransfer.setData('application/json', JSON.stringify(dragData));
  event.dataTransfer.setData(
    'conversationId',
    props.conversation.id.toString()
  );
  event.dataTransfer.setData(
    'conversationData',
    JSON.stringify(props.conversation)
  );

  event.target.classList.add('dragging');
  emit('drag-start', props.conversation.id, props.stageName, event);
};

const handleDragEnd = event => {
  event.target.classList.remove('dragging');
};
</script>

<template>
  <div
    class="kanban-card bg-n-solid-3 rounded-lg border border-n-weak cursor-pointer transition-all duration-150 hover:border-n-slate-7 overflow-hidden"
    :class="{
      'ring-1 ring-n-ruby-9/50': hasSLAViolation,
    }"
    draggable="true"
    @click="handleCardClick"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Header: Inbox + Timer -->
    <div class="flex items-center justify-between px-3 py-1.5 bg-n-alpha-1">
      <span class="text-[10px] font-medium text-n-slate-10 truncate">
        {{ inbox?.name || 'Inbox' }}
      </span>
      <div class="flex items-center gap-1.5">
        <span
          v-if="compactTime"
          class="text-[10px] font-medium"
          :class="hasSLAViolation ? 'text-n-ruby-11' : 'text-n-slate-10'"
        >
          {{ compactTime }}
        </span>
        <button
          v-if="isHovered"
          class="p-0.5 text-n-slate-10 hover:text-n-ruby-11 rounded transition-colors"
          title="Remover do funil"
          @click="handleRemoveFromFunnel"
        >
          <fluent-icon icon="delete" size="12" />
        </button>
      </div>
    </div>

    <!-- Conteúdo -->
    <div class="px-3 py-2.5">
      <!-- Contato -->
      <div class="flex items-center gap-2.5">
        <div class="flex-shrink-0">
          <img
            v-if="currentContact?.thumbnail"
            :src="currentContact.thumbnail"
            :alt="currentContact.name"
            class="w-8 h-8 rounded-full object-cover"
          />
          <div
            v-else
            class="w-8 h-8 rounded-full bg-n-brand/20 text-n-blue-text text-xs flex items-center justify-center font-semibold"
          >
            {{ currentContact?.name?.charAt(0) || 'U' }}
          </div>
        </div>

        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-n-slate-12 truncate">
            {{ currentContact?.name || 'Usuário' }}
          </p>
          <p
            v-if="assignedAgents.length > 0"
            class="text-[10px] text-n-slate-10 truncate mt-0.5"
          >
            {{ visibleAgents[0]?.name }}
          </p>
        </div>

        <!-- Status Badge -->
        <span
          class="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide flex-shrink-0"
          :class="[statusColor]"
        >
          {{ statusLabel }}
        </span>
      </div>

      <!-- Mensagem Preview -->
      <p
        v-if="messagePreview"
        class="text-xs text-n-slate-10 line-clamp-1 mt-2"
      >
        {{ messagePreview }}
      </p>

      <!-- Valor + Agendamento (só se tiver dados) -->
      <div
        v-if="formattedTotalValue || compactAppointment"
        class="flex items-center gap-3 mt-2 text-[10px]"
      >
        <span v-if="formattedTotalValue" class="font-semibold text-n-teal-11">
          {{ formattedTotalValue }}
        </span>
        <span v-if="compactAppointment" class="text-n-slate-10">
          {{ compactAppointment }}
        </span>
      </div>

      <!-- Tags -->
      <div
        v-if="activeLabels.length > 0"
        class="flex items-center gap-1 flex-wrap mt-2"
      >
        <span
          v-for="label in activeLabels.slice(0, 3)"
          :key="label.id"
          class="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium"
          :style="{
            backgroundColor: label.color + '20',
            color: label.color,
          }"
        >
          {{ label.title }}
        </span>
        <span
          v-if="activeLabels.length > 3"
          class="text-[10px] text-n-slate-10"
        >
          +{{ activeLabels.length - 3 }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-card.dragging {
  opacity: 0.5;
  transform: rotate(1deg);
}

.kanban-card:hover {
  transform: translateY(-1px);
}
</style>
